<template>
  <div v-if="variant === 'store'" class="coin-store-layout">
    <div class="coin-store-workspace">
      <div v-if="isLoading" class="store-page-skeleton">
        <div class="skeleton store-page-skeleton-main"></div>
        <div class="skeleton store-page-skeleton-side"></div>
        <div class="skeleton store-page-skeleton-packages"></div>
      </div>

      <template v-else>
        <section class="coin-summary-panel">
          <div class="coin-panel-heading">
            <h2>I tuoi Coin MyStudio</h2>
            <span class="store-plan-chip" :class="currentPlanClass">
              <i class="fa-solid fa-crown"></i>
              Piano {{ currentPlanLabel }}
            </span>
          </div>

          <div class="coin-summary-content">
            <div class="coin-balance-card">
              <span class="temporary-coin-stack is-large" aria-hidden="true">
                <i class="fa-solid fa-coins"></i>
              </span>
              <p>
                Hai <strong>{{ creditiDisponibili }}</strong>
                <b>Coin MyStudio</b>
                <small>
                  {{ creditiDisponibili === 1 ? "disponibile" : "disponibili" }}
                </small>
              </p>
            </div>

            <div class="coin-usage-panel">
              <h3>Come si utilizzano i Coin?</h3>
              <div class="coin-rule-list">
                <div class="coin-rule-row">
                  <span class="coin-rule-icon coin-rule-icon--product">
                    <ProductIcon product="bilancio" />
                  </span>
                  <span>1 cliente in un Bilancio Analitico:</span>
                  <strong>{{ creditiPerBilancio }} Coin</strong>
                </div>
                <div class="coin-rule-row">
                  <span class="coin-rule-icon coin-rule-icon--product">
                    <ProductIcon product="prospetti" />
                  </span>
                  <span>1 cliente in un Prospetto Paghe:</span>
                  <strong>{{ creditiPerProspettoPaghe }} Coin</strong>
                </div>
                <div class="coin-rule-row">
                  <span class="coin-rule-icon coin-rule-icon--product">
                    <ProductIcon product="f24" />
                  </span>
                  <span>1 cliente in un Modello F24:</span>
                  <strong>{{ creditiPerF24 }} Coin</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside class="subscription-preview-card">
          <h2>Piani di abbonamento</h2>
          <span class="subscription-preview-icon" aria-hidden="true">
            <i class="fa-solid fa-calendar-days"></i>
          </span>
          <p>
            Scopri i piani disponibili per il tuo studio e confronta le
            soluzioni.
          </p>
          <button
            type="button"
            disabled
            title="Confronto dei piani disponibile prossimamente"
          >
            Vai ai piani
            <i class="fa-solid fa-arrow-right"></i>
          </button>
        </aside>

        <section class="coin-packages-panel">
          <div class="packages-heading">
            <h2>Ricarica Coin MyStudio</h2>
            <p>Scegli il pacchetto più adatto alle tue esigenze.</p>
          </div>

          <div class="store-conversion-options">
            <button
              v-for="option in conversionOptions"
              :key="option.value"
              type="button"
              class="store-conversion-option"
              :class="{ selected: selectedConversion === option.value }"
              @click="selectConversion(option.value)"
            >
              <i
                v-if="selectedConversion === option.value"
                class="fa-solid fa-circle-check selected-package-check"
                aria-hidden="true"
              ></i>
              <span
                class="temporary-coin-stack"
                :class="`coin-stack-${option.value}`"
                aria-hidden="true"
              >
                <i class="fa-solid fa-coins"></i>
              </span>
              <strong>{{ option.label }}</strong>
              <small>{{ option.value }} Coin</small>
            </button>
          </div>

          <button
            class="store-purchase-button"
            @click="purchaseConversion"
            :disabled="guestMode || isProcessing || !puoFareAcquisti"
          >
            <span v-if="isProcessing">
              <span class="spinner"></span> Elaborazione...
            </span>
            <span v-else-if="guestMode">Registrati per acquistare</span>
            <span v-else>
              <i class="fa-solid fa-cart-shopping"></i>
              Ricarica
            </span>
          </button>

          <div
            class="store-purchase-feedback"
            :class="{
              'is-empty': !message && (guestMode || puoFareAcquisti),
              'is-success': message && messageType === 'success',
              'is-error': message && messageType === 'error',
            }"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <transition name="purchase-feedback">
              <p v-if="message" class="purchase-message">
                <i
                  :class="[
                    'fa-solid',
                    messageType === 'success'
                      ? 'fa-circle-check'
                      : 'fa-circle-exclamation',
                  ]"
                  aria-hidden="true"
                ></i>
                <span>{{ message }}</span>
              </p>
            </transition>
            <p
              v-if="!message && !guestMode && !puoFareAcquisti"
              class="cant-purchase-message"
            >
              Contatta un Amministratore per effettuare acquisti.
            </p>
          </div>
        </section>
      </template>
    </div>
  </div>

  <div v-else class="store-card">
    <h2 class="store-title">Coin MyStudio</h2>
    <div v-if="isLoading" class="skeleton-loader">
      <div class="skeleton skeleton-text"></div>
      <div class="skeleton skeleton-text"></div>
      <div class="skeleton skeleton-options"></div>
      <div class="skeleton skeleton-button"></div>
    </div>
    <div v-else>
      <div class="store-info">
        <p v-if="guestMode" class="guest-store-message"></p>
        <p v-else>
          <i class="fa-solid fa-coins"></i>
          Hai <strong>{{ creditiDisponibili }}</strong>
          <span class="mystudiocoin"> Coin MyStudio</span>
          {{ creditiDisponibili === 1 ? "disponibile" : "disponibili" }}
        </p>
        <div class="conversion-rules">
          <div class="rule-row">
            <span class="label">1 cliente in un Bilancio Analitico:</span>
            <span class="value">{{ creditiPerBilancio }} Coin</span>
          </div>
          <div class="rule-row">
            <span class="label">1 cliente in un Prospetto Paghe:</span>
            <span class="value">{{ creditiPerProspettoPaghe }} Coin</span>
          </div>
          <div class="rule-row">
            <span class="label">1 cliente in un Modello F24:</span>
            <span class="value">{{ creditiPerF24 }} Coin</span>
          </div>
        </div>
      </div>
      <div class="conversion-options">
        <div
          v-for="option in conversionOptions"
          :key="option.value"
          class="conversion-option"
          :class="{ selected: selectedConversion === option.value }"
          @click="selectConversion(option.value)"
        >
          <h4>
            {{ option.label }}
            <i v-if="option.value !== 1" class="fa-solid fa-coins"></i>
          </h4>
          <p>
            {{ option.value }} Coin
            <span>{{ getPrezzo(option.value) }}€</span>
          </p>
        </div>
      </div>
      <button
        class="purchase-button"
        @click="purchaseConversion"
        :disabled="guestMode || isProcessing || !puoFareAcquisti"
      >
        <span v-if="isProcessing">
          <span class="spinner"></span> Elaborazione...
        </span>
        <span v-else-if="guestMode">Registrati per acquistare</span>
        <span v-else>Ricarica</span>
      </button>
      <p v-if="guestMode" class="cant-purchase-message">
        Accedi o crea un account per completare l'acquisto.
      </p>
      <p v-else-if="!puoFareAcquisti" class="cant-purchase-message">
        Contatta un Amministratore per effettuare acquisti.
      </p>
      <p v-if="message" class="purchase-message">{{ message }}</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import ProductIcon from "@/components/ProductIcon.vue";

export default {
  name: "CoinPurchase",
  components: {
    ProductIcon,
  },
  props: {
    guestMode: { type: Boolean, default: false },
    variant: { type: String, default: "compact" },
    currentPlan: { type: String, default: "Base" },
  },
  data() {
    return {
      isLoading: true,
      selectedConversion: 1,
      conversionOptions: [
        { label: "Singolo Coin", value: 1 },
        { label: "Pacchetto 10", value: 10 },
        { label: "Pacchetto 50", value: 50 },
        { label: "Pacchetto 100", value: 100 },
      ],
      userEmail: "",
      message: "",
      messageType: "",
      creditiDisponibili: null,
      isProcessing: false,
      puoFareAcquisti: false,
      creditiPerF24: 0,
      creditiPerProspettoPaghe: 0,
      creditiPerBilancio: 0,
      coinToEuro: 0,
      prezzoPacchetto10: 0,
      prezzoPacchetto50: 0,
      prezzoPacchetto100: 0,
    };
  },
  async mounted() {
    if (this.guestMode) {
      this.creditiDisponibili = 0;
      this.puoFareAcquisti = false;
      await this.fetchPrezziConversione();
    } else {
      await Promise.all([
        this.fetchConversionCount(),
        this.fetchPrezziConversione(),
        this.checkUserPermission(),
      ]);
    }
    this.isLoading = false;
  },
  computed: {
    calcolaPrezzo() {
      return this.getPrezzo(this.selectedConversion);
    },
    currentPlanLabel() {
      const plan = String(this.currentPlan || "Base").toLowerCase();
      return { base: "Base", plus: "Plus", premium: "Premium" }[plan] || "Base";
    },
    currentPlanClass() {
      return `is-${String(this.currentPlan || "base").toLowerCase()}`;
    },
  },
  methods: {
    selectConversion(value) {
      this.message = "";
      this.messageType = "";
      this.selectedConversion = value;
    },
    getPrezzo(value) {
      switch (value) {
        case 1:
          return this.coinToEuro;
        case 10:
          return this.prezzoPacchetto10;
        case 50:
          return this.prezzoPacchetto50;
        case 100:
          return this.prezzoPacchetto100;
        default:
          return 0;
      }
    },
    async checkUserPermission() {
      if (this.guestMode) {
        this.puoFareAcquisti = false;
        return;
      }

      this.userEmail = localStorage.getItem("userEmail");
      if (!this.userEmail) {
        this.puoFareAcquisti = false;
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/user_permission`, {
          params: { email: this.userEmail },
        });
        this.puoFareAcquisti = response.data.puo_fare_acquisti;
      } catch (error) {
        console.error("Errore permessi acquisto:", error);
        this.puoFareAcquisti = false;
      }
    },
    async purchaseConversion() {
      if (this.guestMode) {
        this.message = "Registrati o accedi per acquistare.";
        this.messageType = "error";
        setTimeout(() => {
          this.message = "";
          this.messageType = "";
        }, 3000);
        return;
      }

      if (!this.puoFareAcquisti) {
        this.message = "Non hai i permessi per effettuare acquisti.";
        this.messageType = "error";
        setTimeout(() => {
          this.message = "";
          this.messageType = "";
        }, 3000);
        return;
      }
      this.isProcessing = true;
      this.userEmail = localStorage.getItem("userEmail");
      if (!this.userEmail) {
        this.message = "Devi essere loggato per acquistare.";
        this.messageType = "error";
        this.isProcessing = false;
        setTimeout(() => {
          this.message = "";
          this.messageType = "";
        }, 3000);
        return;
      }
      const coins = this.selectedConversion;
      const euroPagati = this.calcolaPrezzo;
      try {
        if (process.env.VUE_APP_USE_STRIPE === "true") {
          const response = await axios.post(
            `${API_BASE_URL}/create-checkout-session`,
            {
              email: this.userEmail,
              crediti_acquistati: coins,
              euro_pagati: euroPagati,
            }
          );
          window.location.href = response.data.url;
        } else {
          const response = await axios.post(`${API_BASE_URL}/purchase`, {
            email: this.userEmail,
            crediti_acquistati: coins,
            euro_pagati: euroPagati,
          });
          this.message =
            response.data.message ||
            `Acquisto completato! ${coins} Coin aggiunto${
              coins === 1 ? "" : "i"
            }.`;
          this.messageType = "success";
          await this.fetchConversionCount();
          this.$emit("coins-updated");
          setTimeout(() => {
            this.message = "";
            this.messageType = "";
          }, 6000);
        }
      } catch (error) {
        console.error("Errore acquisto:", error);
        this.message = "Errore nell'acquisto.";
        this.messageType = "error";
        setTimeout(() => {
          this.message = "";
          this.messageType = "";
        }, 3000);
      } finally {
        this.isProcessing = false;
      }
    },
    async fetchConversionCount() {
      if (this.guestMode) {
        this.creditiDisponibili = 0;
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/get_conversion_count`
        );
        this.creditiDisponibili = response.data.crediti_disponibili;
      } catch (error) {
        console.error("Errore contatore crediti:", error);
      }
    },
    async fetchPrezziConversione() {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/getPrezziConversione`
        );
        this.creditiPerF24 = response.data.crediti_per_f24;
        this.creditiPerProspettoPaghe =
          response.data.crediti_per_prospetto_paghe;
        this.creditiPerBilancio = response.data.crediti_per_bilancio;
        this.coinToEuro = response.data.prezzo_per_credito;
        this.prezzoPacchetto10 = response.data.prezzo_pacchetto_10;
        this.prezzoPacchetto50 = response.data.prezzo_pacchetto_50;
        this.prezzoPacchetto100 = response.data.prezzo_pacchetto_100;
      } catch (error) {
        console.error("Errore prezzi conversione:", error);
      }
    },
  },
};
</script>

<style scoped>
.coin-store-layout {
  display: flex;
  align-items: stretch;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: auto;
  min-height: 36rem;
  padding: clamp(0.7rem, 1.2vw, 1rem);
  border: 1px solid #dde5ea;
  border-radius: 16px;
  background: linear-gradient(145deg, #f5f7f8 0%, #eef2f4 100%);
  box-shadow: 0 12px 30px rgba(31, 49, 64, 0.07);
  color: #14243d;
  font-family: "Inter", "Segoe UI Variable Text", "Segoe UI", sans-serif;
}

.coin-store-workspace {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(18rem, 1.15fr);
  grid-template-rows: minmax(17rem, auto) minmax(16rem, auto);
  gap: clamp(0.65rem, 1.5vh, 1.1rem);
  box-sizing: border-box;
  width: 100%;
  height: auto;
  min-height: 34rem;
}

.coin-summary-panel,
.subscription-preview-card,
.coin-packages-panel {
  box-sizing: border-box;
  border: 1px solid #e1e7eb;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(31, 49, 64, 0.045);
}

.coin-summary-panel {
  display: flex;
  height: 100%;
  min-height: 0;
  padding: clamp(0.8rem, 1.7vh, 1.25rem) clamp(1rem, 1.7vw, 1.45rem);
  overflow: hidden;
  flex-direction: column;
}

.coin-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: clamp(0.55rem, 1.2vh, 0.9rem);
}

.coin-panel-heading h2,
.subscription-preview-card h2,
.packages-heading h2 {
  margin: 0;
  color: #14243d;
  font-size: 1.12rem;
  font-weight: 750;
  line-height: 1.2;
}

.store-plan-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 1rem;
  border-radius: 999px;
  background: #f4f5f6;
  color: #5d6876;
  font-size: 0.82rem;
  font-weight: 750;
  white-space: nowrap;
}

.store-plan-chip.is-premium {
  background: linear-gradient(135deg, #fff7df, #ffefc8);
  color: #a36b00;
}

.store-plan-chip.is-plus {
  background: linear-gradient(135deg, #eaf3ff, #dcecff);
  color: #1859a8;
}

.coin-summary-content {
  display: grid;
  grid-template-columns: minmax(15rem, 1.05fr) minmax(18rem, 1.25fr);
  flex: 1 1 0;
  gap: clamp(0.8rem, 1.4vw, 1.25rem);
  min-height: 0;
}

.coin-balance-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 0;
  padding: clamp(0.75rem, 1.5vh, 1.1rem);
  border-radius: 11px;
  background: linear-gradient(135deg, #eaf8f0, #f4fbf7);
}

.temporary-coin-stack {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f5a623;
  font-size: 2rem;
  line-height: 1;
}

.temporary-coin-stack.is-large {
  flex: 0 0 auto;
  font-size: 3rem;
}

.coin-balance-card p {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: baseline;
  column-gap: 0.3rem;
  margin: 0;
  color: #52657a;
  font-size: 1.25rem;
  line-height: 1.25;
  text-align: left;
}

.coin-balance-card p strong {
  color: #087a52;
  font-size: 2.5rem;
  line-height: 1;
}

.coin-balance-card p b {
  color: #24364f;
  font-size: 1.12rem;
}

.coin-balance-card p small {
  grid-column: 1 / -1;
  color: #68798b;
  font-size: 1rem;
  font-weight: 400;
  text-align: left;
}

.coin-usage-panel {
  padding-left: 1.35rem;
  border-left: 1px solid #e1e7eb;
  text-align: left;
}

.coin-usage-panel h3 {
  margin: 0 0 0.65rem;
  color: #14243d;
  font-size: 0.96rem;
  text-align: left;
}

.coin-rule-list {
  display: flex;
  flex-direction: column;
}

.coin-rule-row {
  display: grid;
  grid-template-columns: 2.35rem minmax(0, 15.5rem) auto;
  align-items: center;
  justify-content: start;
  gap: 0.65rem;
  min-height: clamp(2.05rem, 4.4vh, 2.55rem);
  border-bottom: 1px solid #e9edf0;
  color: #52657a;
  font-size: 0.78rem;
  text-align: left;
}

.coin-rule-row:last-child {
  border-bottom: 0;
}

.coin-rule-icon {
  display: inline-flex;
  width: 2.35rem;
  height: 2.35rem;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eef8f2;
  color: #29425d;
  font-size: 1.08rem;
}

.coin-rule-icon--product {
  overflow: hidden;
  background: transparent;
}

.coin-rule-icon--product .product-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.coin-rule-row strong {
  color: #293a52;
  font-size: 0.78rem;
  white-space: nowrap;
}

.subscription-preview-card {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: flex-start;
  padding: clamp(0.85rem, 1.8vh, 1.35rem) clamp(1rem, 1.8vw, 1.45rem);
  overflow: hidden;
  border-color: #cfe7da;
  background: linear-gradient(145deg, #fbfefc, #f3faf6);
}

.subscription-preview-icon {
  display: inline-flex;
  width: clamp(3.2rem, 7vh, 4.2rem);
  height: clamp(3.2rem, 7vh, 4.2rem);
  align-items: center;
  justify-content: center;
  margin: clamp(0.45rem, 1.2vh, 0.8rem) 0 clamp(0.35rem, 0.8vh, 0.55rem);
  border-radius: 999px;
  background: #e5f5eb;
  color: #0f8559;
  font-size: 1.65rem;
}

.subscription-preview-card p {
  margin: 0;
  color: #607186;
  font-size: 0.9rem;
  line-height: 1.45;
  text-align: left;
}

.subscription-preview-card button {
  display: inline-flex;
  width: 100%;
  min-height: 2.55rem;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  margin-top: auto;
  border: 1px solid #0d8157;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.58);
  color: #0b6e4a;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 750;
  opacity: 0.64;
  cursor: not-allowed;
}

.coin-packages-panel {
  grid-column: 1 / -1;
  display: flex;
  height: 100%;
  min-height: 0;
  padding: clamp(0.7rem, 1.4vh, 1.1rem) clamp(1rem, 1.7vw, 1.4rem)
    clamp(0.55rem, 1vh, 0.9rem);
  overflow: hidden;
  flex-direction: column;
}

.packages-heading p {
  margin: 0.25rem 0 0;
  color: #68798b;
  font-size: 0.84rem;
}

.store-conversion-options {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  flex: 1 1 0;
  min-height: 0;
  gap: 0.85rem;
  margin-top: clamp(0.55rem, 1.2vh, 0.9rem);
}

.store-conversion-option {
  position: relative;
  display: flex;
  min-width: 0;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0.3rem, 0.8vh, 0.5rem);
  padding: clamp(0.55rem, 1.1vh, 0.8rem);
  border: 1px solid #dfe5ea;
  border-radius: 10px;
  background: #ffffff;
  color: #14243d;
  font: inherit;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.store-conversion-option:hover {
  border-color: #bcd8c9;
  box-shadow: 0 8px 18px rgba(15, 71, 50, 0.08);
  transform: translateY(-1px);
}

.store-conversion-option.selected {
  border-color: #16895c;
  background: linear-gradient(145deg, #eefaf3, #f8fcfa);
  box-shadow: 0 0 0 1px rgba(22, 137, 92, 0.1);
}

.selected-package-check {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  color: #16895c;
  font-size: 1.1rem;
}

.store-conversion-option strong {
  font-size: 0.92rem;
}

.store-conversion-option small {
  color: #52657a;
  font-size: 0.82rem;
}

.coin-stack-1 {
  font-size: 1.75rem;
}

.coin-stack-10 {
  font-size: 2rem;
}

.coin-stack-50 {
  font-size: 2.25rem;
}

.coin-stack-100 {
  font-size: 2.5rem;
}

.store-purchase-button {
  display: flex;
  width: min(18rem, 100%);
  min-height: clamp(2.45rem, 5vh, 2.9rem);
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin: clamp(0.8rem, 1.6vh, 1.15rem) auto 0;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #16864f, #21964e);
  color: #ffffff;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 750;
  cursor: pointer;
}

.store-purchase-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #117344, #188541);
}

.store-purchase-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.store-purchase-feedback {
  display: flex;
  min-height: 2.15rem;
  align-items: center;
  justify-content: center;
  margin-top: 0.35rem;
}

.store-purchase-feedback .purchase-message,
.store-purchase-feedback .cant-purchase-message {
  margin: 0 !important;
  line-height: 1.35;
}

.store-purchase-feedback.is-empty {
  min-height: 2.15rem;
  margin-top: 0.35rem;
}

.store-purchase-feedback .purchase-message {
  display: inline-flex;
  min-height: 1.85rem;
  align-items: center;
  justify-content: center;
  gap: 0.42rem;
  box-sizing: border-box;
  padding: 0.32rem 0.72rem;
  border: 1px solid #b9dec9;
  border-radius: 999px;
  background: #edf9f2;
  color: #176b43;
  font-size: 0.78rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(23, 107, 67, 0.08);
}

.store-purchase-feedback.is-error .purchase-message {
  border-color: #efc3c3;
  background: #fff1f1;
  color: #a93232;
  box-shadow: 0 4px 12px rgba(169, 50, 50, 0.07);
}

.purchase-feedback-enter-active,
.purchase-feedback-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.purchase-feedback-enter,
.purchase-feedback-leave-to {
  opacity: 0;
  transform: translateY(-0.2rem);
}

.store-page-skeleton {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(18rem, 1.15fr);
  grid-template-rows: minmax(17rem, auto) minmax(16rem, auto);
  grid-row: 1 / -1;
  height: auto;
  min-height: 34rem;
  gap: 1.2rem;
  grid-column: 1 / -1;
}

.store-page-skeleton-main,
.store-page-skeleton-side {
  min-height: 17.5rem;
}

.store-page-skeleton-packages {
  grid-column: 1 / -1;
  min-height: 18rem;
}

@media (min-width: 1051px) and (max-height: 900px) {
  .coin-balance-card {
    margin-bottom: 0.4rem;
  }

  .coin-rule-list {
    padding-bottom: 0.4rem;
  }
}

@media (max-width: 1050px) {
  .coin-store-workspace,
  .store-page-skeleton {
    grid-template-columns: 1fr;
  }

  .coin-store-workspace {
    height: auto;
    grid-template-rows: auto;
  }

  .store-page-skeleton {
    height: auto;
    grid-template-rows: auto;
  }

  .coin-summary-panel,
  .subscription-preview-card,
  .coin-packages-panel {
    height: auto;
    min-height: 17.5rem;
    overflow: visible;
  }

  .coin-summary-content {
    grid-template-columns: 1fr;
  }

  .coin-usage-panel {
    padding-top: 1rem;
    padding-left: 0;
    border-top: 1px solid #e1e7eb;
    border-left: 0;
  }

  .coin-packages-panel,
  .store-page-skeleton-packages {
    grid-column: 1;
  }

  .store-conversion-options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 620px) {
  .coin-panel-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .coin-balance-card {
    align-items: flex-start;
  }

  .coin-balance-card p {
    grid-template-columns: auto 1fr;
  }

  .coin-balance-card p b {
    grid-column: 1 / -1;
  }

  .store-conversion-options {
    grid-template-columns: 1fr;
  }
}

.store-card {
  width: 53rem;
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 2.5rem 4.5rem;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  min-height: 350px;
}
.store-title {
  position: absolute;
  top: 0;
  left: 0;
  user-select: none;
  background: #fcedc1;
  color: #115826;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 8px 0 8px 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  font-family: "Segoe UI", sans-serif;
  z-index: 10;
  cursor: default;
}
.store-info p {
  font-size: 1.1rem;
  margin-bottom: 0.6rem;
}

.guest-store-message {
  color: #52645c;
  font-weight: 700;
}

.store-info i {
  color: #f9a825;
  margin-right: 0.4rem;
}
.conversion-rules {
  width: 20rem;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  color: #2c3e50;
  margin: 0 auto 0.5rem;
}

.rule-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e0e0e0;
}

.rule-row:last-child {
  border-bottom: none;
}

.conversion-rules .label {
  font-weight: 600;
  color: #34495e;
}

.conversion-rules .value {
  font-weight: 400;
  color: #555555;
}
.conversion-options {
  display: flex;
  gap: 1.4rem;
  margin: 3rem 0 2rem 0;
  flex-wrap: wrap;
  justify-content: center;
}
.conversion-option {
  border: 2px solid #dedede;
  border-radius: 10px;
  padding: 1rem 1.2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 150px;
  background-color: #fafafa;
}
.conversion-option:hover {
  border-color: #489c4c8f;
  background-color: #eef8ee;
}
.conversion-option.selected {
  border-color: #489c4c8f;
  background-color: #d1f0d1;
  box-shadow: 0 0 10px rgba(46, 125, 50, 0.2);
}
.conversion-option h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.conversion-option p {
  margin-top: 0.5rem;
  font-size: 0.95rem;
  color: #333;
}
.purchase-button {
  background-color: #2e7d32;
  color: white;
  padding: 0.8rem 1.8rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s;
}
.purchase-button:hover {
  background-color: #1b5e20;
}
.purchase-button:disabled {
  background-color: #9e9e9e;
  cursor: not-allowed;
}
.purchase-message {
  margin-top: 1.6rem;
  font-weight: bold;
  color: #2e7d32;
  text-align: center;
  margin-bottom: 0 !important;
}
.cant-purchase-message {
  margin-top: 1.6rem;
  font-weight: normal;
  color: #8d8d8d;
  text-align: center;
  margin-bottom: 0 !important;
}
.spinner {
  border: 2px solid white;
  border-top-color: transparent;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  display: inline-block;
  margin-right: 0.5rem;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.mystudiocoin {
  font-weight: bold;
}
.skeleton-loader {
  width: 53rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}
.skeleton {
  background: #f0f0f0;
  border-radius: 4px;
  animation: pulse 1.5s infinite ease-in-out;
}
.skeleton-text {
  height: 1.2rem;
  width: 60%;
  margin-bottom: 0.5rem;
}
.skeleton-options {
  height: 100px;
  width: 100%;
}
.skeleton-button {
  height: 2.5rem;
  width: 120px;
  align-self: center;
}
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
